import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Popover, Tab, Tabs, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useCampground } from "@/contexts/AppConfigContext";
import { useCurrency } from "@/hooks/useCurrency";
import { getCurrencySymbol } from "@/utils/pricing";
import TextButton from "./TextButton";

export default function LanguageSwitcher() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLocale = i18n.locale;
  const [open, setOpen] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { data: campgroundData } = useCampground();
  const { currencyCode, setCurrencyCode, currencies } = useCurrency();
  const [searchParams] = useSearchParams();

  const currencySymbol = useMemo(() => {
    if (!currencyCode) return "";
    return getCurrencySymbol(currencyCode, currentLocale);
  }, [currencyCode, currentLocale]);

  const buttonLabel = useMemo(() => {
    return `${currentLocale.toUpperCase()}, ${currencySymbol}`;
  }, [currentLocale, currencySymbol]);

  const switchLanguage = (locale: string) => {
    i18n.activate(locale);
    const search = searchParams.toString();
    const path = location.pathname.replace(/^\/[^/]+/, `/${locale}`);
    navigate(`${path}${search ? `?${search}` : ""}`, { replace: true });
  };

  return (
    <>
      <TextButton
        ref={(el) => setAnchorEl(el)}
        onClick={() => setOpen(true)}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        variant="text"
        sx={{
          color: "primary.main",
          textDecoration: "underline",
          fontWeight: 500,
          width: "fit-content",
        }}
      >
        {buttonLabel}
      </TextButton>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: "background.paper",
            border: 1,
            borderColor: "divider",
            minWidth: { xs: 300, md: 350 },
          }}
        >
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label={t`Language`} />
            <Tab label={t`Currency`} />
          </Tabs>
          <Box sx={{ mt: 2 }}>
            {tabValue === 0 && (
              <Box>
                {campgroundData.languages.map((lang) => (
                  <Box
                    key={lang.code}
                    onClick={() => switchLanguage(lang.code)}
                    sx={{
                      bgcolor:
                        currentLocale === lang.code ? "background.default" : "background.paper",
                      borderRadius: 5,
                      py: 2,
                      px: 3,
                      mb: 1,
                      cursor: "pointer",
                      border: 2,
                      borderColor: currentLocale === lang.code ? "primary.main" : "divider",
                    }}
                  >
                    <Typography variant="body1">{lang.name}</Typography>
                  </Box>
                ))}
              </Box>
            )}
            {tabValue === 1 && (
              <Box>
                {currencies.map((curr) => (
                  <Box
                    key={curr.code}
                    onClick={() => setCurrencyCode(curr.code)}
                    sx={{
                      bgcolor:
                        currencyCode === curr.code ? "background.default" : "background.paper",
                      borderRadius: 5,
                      py: 2,
                      px: 3,
                      mb: 1,
                      cursor: "pointer",
                      border: 2,
                      borderColor: currencyCode === curr.code ? "primary.main" : "divider",
                    }}
                  >
                    <Typography variant="body1">
                      {getCurrencySymbol(curr.code, currentLocale)} {curr.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
